import React from "react";
import styled from "styled-components";
import Select from "react-select";
import {
  FormSection,
  SectionTitle,
  FormGroup,
  Label,
  Input,
  SearchButton,
  ButtonContent,
  LoadingSpinner,
  ErrorMessage,
  InfoMessage,
  AddressCard,
  AddressCardContent,
  FormRow
} from "../../styles/OrderFormStyles";
import { addPatientAddress, importPharmacyAddress } from "../../services/api";

// Add styled submit button
const SubmitButton = styled(SearchButton)`
  margin: 15px auto;
  margin-top: 50px;
  width: 33.33%;
  min-width: 120px;
  padding: 0.75rem 1.5rem;
  background-color: #4caf50;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.1rem;

  &:hover {
    background-color: #45a049;
  }
`;

// Add styled tooltip
const Tooltip = styled.div`
  position: relative;
  display: block;
  width: 100%;

  &:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
`;

// New styled row for address dropdowns
const AddressDropdownRow = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;

  > div:first-child {
    flex: 1; /* Make shipping address take equal space */
    min-width: 0; /* Prevent overflow */
    width: 50%; /* Explicitly set to 50% */
  }

  > div:last-child {
    flex: 1; /* Make pharmacy address take equal space */
    min-width: 0; /* Prevent overflow */
    width: 50%; /* Explicitly set to 50% */
  }
`;

// Styled container for dropdown with button
const PharmacyAddressContainer = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;

  .dropdown-container {
    flex: 1;
    min-width: 0; /* Prevent overflow */
    width: calc(100% - 140px); /* Leave space for button */
  }

  button {
    align-self: flex-end;
    margin-bottom: 0.5rem;
    height: 38px;
    width: 130px; /* Fixed width for button */
    background-color: var(--primary-color, #6366f1);
    white-space: nowrap; /* Prevent button text from wrapping */

    &:hover {
      background-color: var(--primary-dark, #4f46e5);
    }

    &:disabled {
      background-color: var(--disabled-bg, #4b5563);
      cursor: not-allowed;
    }
  }
`;

// New styled component for the primary tag
const PrimaryTag = styled.span`
  background-color: #10b981;
  color: black;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  margin-left: 5px;
  white-space: nowrap;
`;

// New styled component for the imported tag
const ImportedTag = styled.span`
  background-color: #fbbf24;
  color: black;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  margin-left: 5px;
  white-space: nowrap;
`;

// Custom styles for React Select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? "var(--primary-color, #6366f1)" : "#ced4da",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(99, 102, 241, 0.25)" : "none",
    "&:hover": {
      borderColor: "var(--primary-color, #6366f1)"
    },
    fontSize: "1rem",
    minHeight: "38px",
    width: "100%"
  }),
  container: (provided) => ({
    ...provided,
    width: "100%"
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "var(--primary-color, #6366f1)"
      : state.isFocused
      ? "rgba(99, 102, 241, 0.1)"
      : "white",
    color: state.isSelected ? "white" : "black",
    fontSize: "1rem",
    padding: "10px 16px"
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#aaa",
    fontSize: "1rem"
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 10,
    width: "100%"
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 12px"
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "1rem"
  })
};

// Format option label for pharmacy addresses
const formatPharmacyLabel = ({ value, label, isPrimary }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    }}
  >
    <span>{label}</span>
    {isPrimary && <PrimaryTag>Primary</PrimaryTag>}
  </div>
);

// Format option label for shipping addresses
const formatAddressLabel = ({ value, label, isImported }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    }}
  >
    <span>{label}</span>
    {isImported && <ImportedTag>Imported</ImportedTag>}
  </div>
);

const ShippingAddressSection = ({
  formData,
  setFormData,
  errors,
  setErrors,
  patientFound,
  patientAddresses,
  setPatientAddresses,
  states,
  newAddress,
  setNewAddress,
  pharmacies,
  pharmacyAddressSync,
  applyShippingAddressSelection,
  stateChangeNotices = [],
  handleInputChange,
  handleAddressChange,
  handlePharmacyAddressChange
}) => {
  const [submittingAddress, setSubmittingAddress] = React.useState(false);
  const [addressSubmitSuccess, setAddressSubmitSuccess] = React.useState(false);
  const [addressSubmitError, setAddressSubmitError] = React.useState("");
  const [importingAddress, setImportingAddress] = React.useState(false);
  const [importAddressSuccess, setImportAddressSuccess] = React.useState(false);
  const [importAddressError, setImportAddressError] = React.useState("");

  // Name of the pharmacy currently selected for the order
  const selectedPharmacy = pharmacies.find(
    (p) =>
      (p.id || p.pharmacy_id)?.toString() ===
      (formData.pharmacyId || "").toString()
  );
  const selectedPharmacyName = selectedPharmacy?.name || "";

  // Create address options for React Select
  const addressOptions = React.useMemo(() => {
    const options = patientAddresses.map((address) => ({
      value: address?.address_id?.toString() || "",
      label: `${address?.address1} ${
        address?.address2 ? address?.address2 : ""
      }, ${address?.city}, ${address?.state} ${address?.zipcode}`,
      isImported: address?.imported
    }));

    // Add option to create new address
    options.push({ value: "new", label: "+ Add New Address" });
    return options;
  }, [patientAddresses]);

  // Create pharmacy address options for React Select
  const pharmacyAddressOptions = React.useMemo(() => {
    if (!formData.pharmacy_addresses) return [];

    return formData.pharmacy_addresses.map((address) => ({
      value: address?.address_id?.toString() || "",
      label: `${address?.address1} ${
        address?.address2 ? address?.address2 : ""
      }, ${address?.city}, ${address?.state} ${address?.zipcode}`,
      isPrimary: address?.primary
    }));
  }, [formData.pharmacy_addresses]);

  // Create state options for React Select
  const stateOptions = React.useMemo(() => {
    return states.map((state) => ({
      value: state.state_abbreviation,
      label: state.state_name
    }));
  }, [states]);

  // Custom handlers for React Select
  const handleAddressSelectChange = (selectedOption) => {
    setNewAddress(null);
    // Create a synthetic event object for compatibility with existing handler
    const syntheticEvent = {
      target: {
        name: "selectedAddressId",
        value: selectedOption.value
      }
    };

    handleAddressChange(syntheticEvent);
  };

  const handlePharmacyAddressSelectChange = (selectedOption) => {
    // Create a synthetic event object for compatibility with existing handler
    const syntheticEvent = {
      target: {
        name: "selectedPharmacyAddressId",
        value: selectedOption.value
      }
    };
    handlePharmacyAddressChange(syntheticEvent);
  };

  const handleStateSelectChange = (selectedOption) => {
    const selectedState = states.find(
      (state) => state.state_abbreviation === selectedOption.value
    );

    setFormData((prev) => ({
      ...prev,
      newAddress: {
        ...prev.newAddress,
        state: selectedOption.value,
        stateName: selectedState ? selectedState.state_name : ""
      }
    }));
  };

  // Function to handle zip code input - only allow numbers
  const handleZipCodeChange = (e) => {
    // Extract only digits from the input
    const zipValue = e.target.value.replace(/\D/g, "");

    // Create a synthetic event with just the numeric value
    const syntheticEvent = {
      target: {
        name: e.target.name,
        value: zipValue
      }
    };

    // Call the normal input handler with our modified event
    handleInputChange(syntheticEvent);
  };

  React.useEffect(() => {
    if (importAddressSuccess || importAddressError) {
      const timer = setTimeout(() => {
        setImportAddressSuccess(false);
        setImportAddressError("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [importAddressSuccess, importAddressError]);

  // Handle pharmacy address import - imports from the pharmacy selected for the order
  const handleImportAddress = async () => {
    if (!formData.patientId || !formData.selectedPharmacyAddressId) {
      setImportAddressError("Please select a pharmacy address to import");
      return;
    }

    if (!formData.pharmacyId) {
      setImportAddressError("Please select a pharmacy first");
      return;
    }

    setImportingAddress(true);
    setImportAddressError("");

    try {
      const response = await importPharmacyAddress(
        formData.patientId,
        formData.pharmacyId,
        formData.selectedPharmacyAddressId
      );

      console.log("Address import response:", response);
      setImportAddressSuccess(true);

      // Refresh the patient's address list from the response and select
      // the imported address as the shipping address
      const addresses = response.addresses || [];
      const sortedAddresses = [...addresses].sort((a, b) => {
        const aId = a && a.address_id ? parseInt(a.address_id) : 0;
        const bId = b && b.address_id ? parseInt(b.address_id) : 0;
        return bId - aId;
      });
      setPatientAddresses(sortedAddresses);
      setNewAddress(null);

      if (response.address) {
        applyShippingAddressSelection(response.address);
      }
    } catch (error) {
      console.error("Error importing address:", error);
      setImportAddressError(error.message || "Failed to import address");
    } finally {
      setImportingAddress(false);
    }
  };

  // Handler for submitting new address
  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setSubmittingAddress(true);
    setAddressSubmitError("");

    // Validate form fields
    const newErrors = {};
    if (!formData.newAddress.street)
      newErrors["newAddress.street"] = "Street address is required";
    if (!formData.newAddress.city)
      newErrors["newAddress.city"] = "City is required";
    if (!formData.newAddress.state)
      newErrors["newAddress.state"] = "State is required";
    if (!formData.newAddress.zipcode)
      newErrors["newAddress.zipcode"] = "Zip code is required";

    // Update errors and stop if validation fails
    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...errors, ...newErrors });
      setSubmittingAddress(false);
      return;
    }

    try {
      // Prepare payload in the format expected by the API
      const addressPayload = {
        address1: formData.newAddress.street,
        address2: formData.newAddress.address2 || "",
        city: formData.newAddress.city,
        state: formData.newAddress.state,
        zipcode: formData.newAddress.zipcode
      };

      const response = await addPatientAddress(
        formData.patientId,
        addressPayload
      );
      console.log("response", response);
      // Handle successful response - safely access addresses from response
      // The response structure may be response.data.addresses or response.data directly
      const responseData = response?.data || response;
      const addresses = responseData?.addresses || [];
      setPatientAddresses(addresses);
      const savedAddress =
        addresses.length > 0
          ? [...addresses].sort(
              (a, b) => (b.address_id || 0) - (a.address_id || 0)
            )[0]
          : null;
      setNewAddress(savedAddress);
      if (savedAddress) {
        setFormData((prev) => ({
          ...prev,
          isNewAddress: true,
          // Reset the new address form
          newAddress: {
            street: "",
            address2: "",
            city: "",
            state: "",
            zipcode: ""
          }
        }));

        // Select the newly created address as the shipping address
        applyShippingAddressSelection(savedAddress);
      }

      setAddressSubmitSuccess(true);

      setTimeout(() => {
        setAddressSubmitSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error adding address:", error);
      setAddressSubmitError(
        error.response?.data?.error ||
          "Failed to add address. Please try again."
      );
    } finally {
      setSubmittingAddress(false);
    }
  };

  // Get selected address option
  const selectedAddressOption = addressOptions.find(
    (option) => option.value === formData.selectedAddressId?.toString()
  );

  // Get selected pharmacy address option
  const selectedPharmacyOption =
    pharmacyAddressOptions.find(
      (option) => option.value === formData.selectedPharmacyAddressId
    ) || null;

  // Get selected state option for new address form
  const selectedStateOption = stateOptions.find(
    (option) => option.value === formData.newAddress.state
  );

  // Auto-select the most recent address when none is selected yet
  React.useEffect(() => {
    if (
      (!formData.selectedAddressId || formData.selectedAddressId === "") &&
      patientAddresses.length > 0
    ) {
      const sortedAddresses = [...patientAddresses].sort((a, b) => {
        const aId = a && a.address_id ? parseInt(a.address_id) : 0;
        const bId = b && b.address_id ? parseInt(b.address_id) : 0;
        return bId - aId;
      });
      applyShippingAddressSelection(sortedAddresses[0]);
    }
  }, [patientAddresses]);

  if (!patientFound) {
    return null;
  }

  const pharmacySelected = !!formData.pharmacyId;
  const noPharmacyAddresses =
    !formData.pharmacy_addresses || formData.pharmacy_addresses.length === 0;

  return (
    <FormSection>
      <SectionTitle>Shipping Address</SectionTitle>

      <AddressDropdownRow>
        <FormGroup style={{ flex: "1" }}>
          <Label htmlFor="selectedAddressId">Shipping Address</Label>
          <Tooltip>
            <div style={{ position: "relative", width: "100%" }}>
              <Select
                id="selectedAddressId"
                name="selectedAddressId"
                value={
                  newAddress
                    ? {
                        isImported: false,
                        label: newAddress.address1 + (newAddress.address2 || ""),
                        value: newAddress.address_id
                      }
                    : selectedAddressOption
                }
                onChange={handleAddressSelectChange}
                options={addressOptions}
                styles={customSelectStyles}
                placeholder="-- Select Address --"
                className={formData.isNewAddress ? "disabled-select" : ""}
                formatOptionLabel={formatAddressLabel}
              />
            </div>
          </Tooltip>
          {errors.selectedAddressId && (
            <ErrorMessage>{errors.selectedAddressId}</ErrorMessage>
          )}
          {!formData.selectedAddressId && patientAddresses.length > 0 && (
            <InfoMessage>
              Please select an address from the dropdown or add a new one.
            </InfoMessage>
          )}
        </FormGroup>

        {/* Pharmacy Addresses Dropdown with Import Button */}
        <FormGroup style={{ flex: "1" }}>
          <Label htmlFor="selectedPharmacyAddressId">
            Pharmacy Addresses
            {selectedPharmacyName ? ` (${selectedPharmacyName})` : ""}
          </Label>
          <PharmacyAddressContainer>
            <div className="dropdown-container">
              <Select
                id="selectedPharmacyAddressId"
                name="selectedPharmacyAddressId"
                value={selectedPharmacyOption}
                onChange={handlePharmacyAddressSelectChange}
                options={pharmacyAddressOptions}
                styles={customSelectStyles}
                isDisabled={
                  !pharmacySelected ||
                  pharmacyAddressSync.loading ||
                  noPharmacyAddresses
                }
                placeholder="-- Select Pharmacy Address --"
                formatOptionLabel={formatPharmacyLabel}
              />
            </div>
            <SearchButton
              type="button"
              onClick={handleImportAddress}
              disabled={
                importingAddress ||
                pharmacyAddressSync.loading ||
                !formData.selectedPharmacyAddressId ||
                !formData.patientId ||
                !pharmacySelected
              }
            >
              {importingAddress ? (
                <ButtonContent>
                  <LoadingSpinner />
                  Importing...
                </ButtonContent>
              ) : (
                "Import Address"
              )}
            </SearchButton>
          </PharmacyAddressContainer>
          {!pharmacySelected && (
            <InfoMessage>
              Select a pharmacy in Order Request Details above to load its
              addresses.
            </InfoMessage>
          )}
          {pharmacySelected && pharmacyAddressSync.loading && (
            <InfoMessage>
              Syncing addresses from {selectedPharmacyName || "pharmacy"}...
            </InfoMessage>
          )}
          {pharmacySelected &&
            !pharmacyAddressSync.loading &&
            !pharmacyAddressSync.supported && (
              <InfoMessage>
                Address import is not available for{" "}
                {selectedPharmacyName || "this pharmacy"}.
              </InfoMessage>
            )}
          {pharmacySelected &&
            !pharmacyAddressSync.loading &&
            pharmacyAddressSync.error && (
              <ErrorMessage style={{ marginTop: "5px" }}>
                {pharmacyAddressSync.error}
              </ErrorMessage>
            )}
          {pharmacySelected &&
            !pharmacyAddressSync.loading &&
            pharmacyAddressSync.supported &&
            !pharmacyAddressSync.error &&
            noPharmacyAddresses && (
              <InfoMessage>
                No pharmacy addresses available for this patient at{" "}
                {selectedPharmacyName || "this pharmacy"}.
              </InfoMessage>
            )}
          {importAddressSuccess && (
            <InfoMessage style={{ color: "green", marginTop: "10px" }}>
              Address imported successfully!
            </InfoMessage>
          )}
          {importAddressError && (
            <ErrorMessage style={{ marginTop: "5px" }}>
              {importAddressError}
            </ErrorMessage>
          )}
          {stateChangeNotices.map((notice) => (
            <ErrorMessage key={notice} style={{ marginTop: "5px" }}>
              {notice}
            </ErrorMessage>
          ))}
        </FormGroup>
      </AddressDropdownRow>

      {formData.selectedAddressId && formData.selectedAddressId !== "new" && (
        <AddressCard>
          <AddressCardContent>
            <p>
              <strong>Selected Shipping Address:</strong>
              {formData.patientAddress.imported && (
                <ImportedTag style={{ marginLeft: "10px" }}>
                  Imported
                </ImportedTag>
              )}
            </p>
            <p>{formData.patientAddress.street}</p>
            {formData.patientAddress.apt && <p>{formData.patientAddress.apt}</p>}
            <p>
              {formData.patientAddress.city}, {formData.patientAddress.state}{" "}
              {formData.patientAddress.zipcode}
            </p>
          </AddressCardContent>
        </AddressCard>
      )}

      {formData.selectedAddressId === "new" && (
        <FormGroup>
          <SectionTitle>New Shipping Address</SectionTitle>
          <FormRow>
            <div style={{ flex: 1 }}>
              <Label htmlFor="newAddress.street">Street Address</Label>
              <Input
                type="text"
                id="newAddress.street"
                name="newAddress.street"
                value={formData.newAddress.street}
                onChange={handleInputChange}
                placeholder="123 Main St"
                disabled={formData.addressSaved}
              />
              {errors["newAddress.street"] && (
                <ErrorMessage>{errors["newAddress.street"]}</ErrorMessage>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <Label htmlFor="newAddress.address2">
                Apartment, Suite, etc. (optional)
              </Label>
              <Input
                type="text"
                id="newAddress.address2"
                name="newAddress.address2"
                value={formData.newAddress.address2 || ""}
                onChange={handleInputChange}
                placeholder="Apt 4B"
                disabled={formData.addressSaved}
              />
            </div>
          </FormRow>
          <FormRow>
            <div style={{ flex: 2 }}>
              <Label htmlFor="newAddress.city">City</Label>
              <Input
                type="text"
                id="newAddress.city"
                name="newAddress.city"
                value={formData.newAddress.city}
                onChange={handleInputChange}
                placeholder="Miami"
                disabled={formData.addressSaved}
              />
              {errors["newAddress.city"] && (
                <ErrorMessage>{errors["newAddress.city"]}</ErrorMessage>
              )}
            </div>

            <div style={{ flex: 2 }}>
              <Label htmlFor="newAddress.state">State</Label>
              <Select
                id="newAddress.state"
                name="newAddress.state"
                value={selectedStateOption}
                onChange={handleStateSelectChange}
                options={stateOptions}
                styles={customSelectStyles}
                isDisabled={formData.addressSaved}
                placeholder="-- Select State --"
              />
              {errors["newAddress.state"] && (
                <ErrorMessage>{errors["newAddress.state"]}</ErrorMessage>
              )}
            </div>

            <div style={{ flex: 1.5 }}>
              <Label htmlFor="newAddress.zipcode">Zip Code</Label>
              <Input
                type="text"
                id="newAddress.zipcode"
                name="newAddress.zipcode"
                value={formData.newAddress.zipcode}
                onChange={handleZipCodeChange}
                placeholder="33101"
                disabled={formData.addressSaved}
              />
              {errors["newAddress.zipcode"] && (
                <ErrorMessage>{errors["newAddress.zipcode"]}</ErrorMessage>
              )}
            </div>
          </FormRow>

          {/* Submit button for the new address */}
          <SubmitButton
            type="button"
            onClick={handleAddressSubmit}
            disabled={submittingAddress}
          >
            {submittingAddress ? (
              <ButtonContent>
                <LoadingSpinner />
                Saving Address...
              </ButtonContent>
            ) : (
              "Save Address"
            )}
          </SubmitButton>

          {/* Success and error messages */}
          {addressSubmitSuccess && (
            <InfoMessage style={{ color: "green", marginTop: "10px" }}>
              Address added successfully!
            </InfoMessage>
          )}

          {addressSubmitError && (
            <ErrorMessage style={{ marginTop: "10px" }}>
              {addressSubmitError}
            </ErrorMessage>
          )}
        </FormGroup>
      )}
    </FormSection>
  );
};

export default ShippingAddressSection;
